import { Button, Card, Chip, faDigits, Field, Panel } from "@kitchen/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  archivePerformanceCriterion,
  createPerformanceCriterion,
  getMonthlyPerformance,
  getPerformanceCriteria,
  setPerformanceBonusBase,
  setPerformanceScore,
} from "../../api.js";
import { jalaliPayMonthOf } from "@kitchen/schemas";
import { tehranToday } from "../../labels.js";
import { ErrorLine } from "../common.js";

function formatRial(amount: number): string {
  return new Intl.NumberFormat("fa-IR").format(amount) + " ریال";
}

const JALALI_MONTH_NAMES = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

export function PerformanceTab() {
  const queryClient = useQueryClient();
  const currentPayMonth = jalaliPayMonthOf(tehranToday());

  const [year, setYear] = useState<number>(currentPayMonth.year);
  const [month, setMonth] = useState<number>(currentPayMonth.month);

  // Criteria modal state
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  const [newCritName, setNewCritName] = useState("");
  const [newCritWeight, setNewCritWeight] = useState("250");
  const [newCritIsLateness, setNewCritIsLateness] = useState(false);
  const [newCritDesc, setNewCritDesc] = useState("");
  const [critError, setCritError] = useState<string | null>(null);
  const [creatingCrit, setCreatingCrit] = useState(false);

  // Local edits state per person
  const [scoreDrafts, setScoreDrafts] = useState<
    Record<string, Record<string, { score: number; note: string }>>
  >({});
  const [bonusDrafts, setBonusDrafts] = useState<Record<string, { amountRial: number; notes: string }>>({});
  const [savingPersonId, setSavingPersonId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const criteriaQuery = useQuery({
    queryKey: ["performance-criteria"],
    queryFn: getPerformanceCriteria,
  });

  const monthlyQuery = useQuery({
    queryKey: ["performance-monthly", year, month],
    queryFn: () => getMonthlyPerformance(year, month),
  });

  const criteria = criteriaQuery.data ?? [];
  const monthlyList = monthlyQuery.data ?? [];

  const totalWeight = criteria.reduce((sum, c) => sum + c.weightPerMille, 0);

  const handleCreateCriterion = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setCritError(null);
    const weight = Number(newCritWeight);
    if (!newCritName.trim() || isNaN(weight) || weight <= 0 || weight > 1000) {
      setCritError("نام و وزن شاخص (بین ۱ تا ۱۰۰۰ در هزار) معتبر نیست");
      return;
    }
    setCreatingCrit(true);
    try {
      await createPerformanceCriterion({
        name: newCritName.trim(),
        weightPerMille: weight,
        description: newCritDesc.trim(),
        isLateness: newCritIsLateness,
      });
      setNewCritName("");
      setNewCritDesc("");
      setNewCritIsLateness(false);
      await queryClient.invalidateQueries({ queryKey: ["performance-criteria"] });
      await queryClient.invalidateQueries({ queryKey: ["performance-monthly"] });
    } catch (err: unknown) {
      setCritError(err instanceof Error ? err.message : "خطا در ایجاد شاخص");
    } finally {
      setCreatingCrit(false);
    }
  };

  const handleArchiveCriterion = async (id: string) => {
    try {
      await archivePerformanceCriterion(id);
      await queryClient.invalidateQueries({ queryKey: ["performance-criteria"] });
      await queryClient.invalidateQueries({ queryKey: ["performance-monthly"] });
    } catch (err: unknown) {
      setCritError(err instanceof Error ? err.message : "خطا در بایگانی شاخص");
    }
  };

  const getScoreValue = (personId: string, criterionId: string, originalScore?: number) => {
    const draft = scoreDrafts[personId]?.[criterionId];
    if (draft !== undefined) return draft.score;
    return originalScore ?? 3;
  };

  const getNoteValue = (personId: string, criterionId: string, originalNote?: string) => {
    const draft = scoreDrafts[personId]?.[criterionId];
    if (draft !== undefined) return draft.note;
    return originalNote ?? "";
  };

  const handleScoreChange = (personId: string, criterionId: string, score: number, note?: string) => {
    setScoreDrafts((prev) => {
      const personScores = { ...(prev[personId] ?? {}) };
      const current = personScores[criterionId] ?? { score: 3, note: "" };
      personScores[criterionId] = {
        score,
        note: note !== undefined ? note : current.note,
      };
      return { ...prev, [personId]: personScores };
    });
  };

  const getBonusAmount = (personId: string, originalAmount: number | null) => {
    const draft = bonusDrafts[personId];
    if (draft !== undefined) return draft.amountRial;
    return originalAmount ?? 0;
  };

  const getBonusNotes = (personId: string, originalNotes: string | null) => {
    const draft = bonusDrafts[personId];
    if (draft !== undefined) return draft.notes;
    return originalNotes ?? "";
  };

  const handleBonusChange = (personId: string, amountRial: number, notes?: string) => {
    setBonusDrafts((prev) => {
      const current = prev[personId] ?? { amountRial: 0, notes: "" };
      return {
        ...prev,
        [personId]: {
          amountRial,
          notes: notes !== undefined ? notes : current.notes,
        },
      };
    });
  };

  const handleSavePerson = async (personId: string) => {
    setSavingPersonId(personId);
    setSaveError(null);
    try {
      // Save any updated scores
      const personScores = scoreDrafts[personId];
      if (personScores) {
        for (const [criterionId, s] of Object.entries(personScores)) {
          await setPerformanceScore({
            personId,
            year,
            month,
            criterionId,
            score: s.score,
            note: s.note,
          });
        }
      }

      // Save bonus base if modified
      const bonusDraft = bonusDrafts[personId];
      if (bonusDraft) {
        await setPerformanceBonusBase({
          personId,
          year,
          month,
          amountRial: bonusDraft.amountRial,
          notes: bonusDraft.notes,
        });
      }

      // Clear drafts for this person
      setScoreDrafts((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== personId)));
      setBonusDrafts((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== personId)));

      await queryClient.invalidateQueries({ queryKey: ["performance-monthly", year, month] });
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "خطا در ذخیره امتیازها");
    } finally {
      setSavingPersonId(null);
    }
  };

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Top action row: Month Selector & Criteria Button */}
      <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <div className="row wrap" style={{ alignItems: "center", gap: "var(--s3)" }}>
          <div className="row" style={{ alignItems: "center", gap: "var(--s2)" }}>
            <span className="b">سال:</span>
            <select
              className="input"
              value={year}
              onChange={(e) => {
                setYear(Number(e.target.value));
              }}
              style={{ width: "90px" }}
            >
              {[1404, 1405, 1406].map((y) => (
                <option key={y} value={y}>
                  {faDigits(y)}
                </option>
              ))}
            </select>
          </div>

          <div className="row" style={{ alignItems: "center", gap: "var(--s2)" }}>
            <span className="b">ماه:</span>
            <select
              className="input"
              value={month}
              onChange={(e) => {
                setMonth(Number(e.target.value));
              }}
              style={{ width: "120px" }}
            >
              {JALALI_MONTH_NAMES.map((name, idx) => (
                <option key={idx + 1} value={idx + 1}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <span className="hint ink2">
            مجموع وزن شاخص‌ها: <strong className="b">{faDigits(totalWeight)}</strong> از ۱۰۰۰ در هزار
          </span>
        </div>

        <Button
          variant="ghost"
          icon="settings"
          onClick={() => {
            setShowCriteriaModal(true);
          }}
        >
          مدیریت شاخص‌های ارزیابی ({faDigits(criteria.length)})
        </Button>
      </div>

      <ErrorLine error={saveError} />

      {/* Monthly Performance Table */}
      <Panel title={`ارزیابی عملکرد پرسنل — ${JALALI_MONTH_NAMES[month - 1] ?? ""} ${faDigits(year)}`}>
        {criteria.length === 0 ? (
          <div className="card stack" style={{ padding: "var(--s4)", textAlign: "center" }}>
            <p className="ink2">هنوز هیچ شاخص ارزیابی فعالی تعریف نشده است.</p>
            <div>
              <Button
                variant="primary"
                onClick={() => {
                  setShowCriteriaModal(true);
                }}
              >
                تعریف اولین شاخص ارزیابی
              </Button>
            </div>
          </div>
        ) : monthlyList.length === 0 ? (
          <p className="ink2" style={{ padding: "var(--s4)" }}>
            پرسنلی برای این ماه یافت نشد.
          </p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>رتبه</th>
                  <th>نام پرسنل</th>
                  <th>سمت</th>
                  {criteria.map((c) => (
                    <th key={c.id} style={{ minWidth: "150px" }}>
                      <div>{c.name}</div>
                      <span className="hint ink2" style={{ fontSize: "0.75rem", fontWeight: "normal" }}>
                        وزن: {faDigits(c.weightPerMille)}‰
                      </span>
                    </th>
                  ))}
                  <th style={{ minWidth: "120px" }}>امتیاز کل (از ۱۰۰۰)</th>
                  <th style={{ minWidth: "140px" }}>مبنای پاداش (ریال)</th>
                  <th>علت / یادداشت پاداش</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {monthlyList.map((p) => {
                  const hasDrafts = Boolean(scoreDrafts[p.personId] || bonusDrafts[p.personId]);
                  return (
                    <tr key={p.personId} data-testid={`performance-row-${p.personId}`}>
                      <td>
                        <Chip tone={p.rank === 1 ? "ok" : "dark"}>#{faDigits(p.rank)}</Chip>
                      </td>
                      <td className="b">{p.displayName}</td>
                      <td>{p.positionName ?? "—"}</td>

                      {/* Criteria inputs */}
                      {criteria.map((c) => {
                        const original = p.scores[c.id];
                        const scoreVal = getScoreValue(p.personId, c.id, original?.score);
                        const noteVal = getNoteValue(p.personId, c.id, original?.note);

                        return (
                          <td key={c.id}>
                            <div className="stack" style={{ gap: "4px" }}>
                              <div className="row" style={{ alignItems: "center", gap: "6px" }}>
                                <select
                                  className="input"
                                  value={scoreVal}
                                  onChange={(e) => {
                                    handleScoreChange(p.personId, c.id, Number(e.target.value), noteVal);
                                  }}
                                  style={{ width: "65px", padding: "2px 6px" }}
                                >
                                  {[1, 2, 3, 4, 5].map((num) => (
                                    <option key={num} value={num}>
                                      {faDigits(num)}
                                    </option>
                                  ))}
                                </select>

                                {c.isLateness && p.suggestedLatenessScore != null && (
                                  <Button
                                    variant="ghost"
                                    onClick={() => {
                                      handleScoreChange(
                                        p.personId,
                                        c.id,
                                        p.suggestedLatenessScore as number,
                                        `پیشنهاد خودکار از ${faDigits(p.lateMinutes)} دقیقه تأخیر`,
                                      );
                                    }}
                                    title={`تأخیر کل: ${faDigits(p.lateMinutes)} دقیقه`}
                                    style={{ padding: "2px 6px", fontSize: "0.75rem" }}
                                  >
                                    پیشنهاد: {faDigits(p.suggestedLatenessScore)}
                                  </Button>
                                )}
                              </div>
                              <input
                                className="input"
                                placeholder="یادداشت..."
                                value={noteVal}
                                onChange={(e) => {
                                  handleScoreChange(p.personId, c.id, scoreVal, e.target.value);
                                }}
                                style={{ fontSize: "0.8rem", padding: "2px 6px" }}
                              />
                            </div>
                          </td>
                        );
                      })}

                      {/* Weighted score */}
                      <td>
                        <strong className="b" style={{ fontSize: "1.1rem" }}>
                          {faDigits(p.weightedTotalPerMille)}
                        </strong>{" "}
                        <span className="ink2" style={{ fontSize: "0.8rem" }}>
                          ‰
                        </span>
                      </td>

                      {/* Bonus base rial */}
                      <td>
                        <input
                          className="input"
                          type="number"
                          step={100000}
                          value={getBonusAmount(p.personId, p.bonusBaseRial)}
                          onChange={(e) => {
                            handleBonusChange(
                              p.personId,
                              Number(e.target.value),
                              getBonusNotes(p.personId, p.bonusNotes),
                            );
                          }}
                          dir="ltr"
                          style={{ width: "130px", textAlign: "right" }}
                        />
                        <span className="hint ink2" style={{ fontSize: "0.75rem" }}>
                          {formatRial(getBonusAmount(p.personId, p.bonusBaseRial))}
                        </span>
                      </td>

                      {/* Bonus note */}
                      <td>
                        <input
                          className="input"
                          placeholder="علت پاداش دستی..."
                          value={getBonusNotes(p.personId, p.bonusNotes)}
                          onChange={(e) => {
                            handleBonusChange(
                              p.personId,
                              getBonusAmount(p.personId, p.bonusBaseRial),
                              e.target.value,
                            );
                          }}
                          style={{ minWidth: "120px" }}
                        />
                      </td>

                      {/* Actions */}
                      <td>
                        <Button
                          variant={hasDrafts ? "primary" : "ghost"}
                          onClick={() => {
                            void handleSavePerson(p.personId);
                          }}
                          disabled={savingPersonId === p.personId || !hasDrafts}
                        >
                          {savingPersonId === p.personId ? "در حال ثبت..." : "ذخیره"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Criteria Management Modal */}
      {showCriteriaModal && (
        <div className="modal-backdrop">
          <div
            className="card modal-card"
            style={{ maxWidth: "750px", width: "95%", maxHeight: "90vh", overflowY: "auto" }}
          >
            <div className="row wrap-between" style={{ alignItems: "center", marginBottom: "var(--s3)" }}>
              <h2 className="t-title">مدیریت شاخص‌های ارزیابی عملکرد</h2>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCriteriaModal(false);
                }}
              >
                بستن
              </Button>
            </div>
            <div className="stack" style={{ gap: "var(--s4)" }}>
              {/* Create Criterion Form */}
              <Card>
                <form
                  className="stack"
                  onSubmit={(e) => {
                    void handleCreateCriterion(e);
                  }}
                >
                  <h3 className="b" style={{ margin: 0 }}>
                    تعریف شاخص جدید
                  </h3>
                  <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "var(--s3)" }}>
                    <Field
                      id="crit-name"
                      label="عنوان شاخص"
                      value={newCritName}
                      onChange={(e) => {
                        setNewCritName(e.target.value);
                      }}
                      placeholder="مثال: دقت در آماده‌سازی، انضباط"
                    />
                    <Field
                      id="crit-weight"
                      label="وزن در هزار (‰)"
                      type="number"
                      value={newCritWeight}
                      onChange={(e) => {
                        setNewCritWeight(e.target.value);
                      }}
                      hint="مثال: ۲۵۰ (معادل ۲۵ درصد)"
                    />
                  </div>
                  <Field
                    id="crit-desc"
                    label="توضیحات و راهنمای ارزیابی"
                    value={newCritDesc}
                    onChange={(e) => {
                      setNewCritDesc(e.target.value);
                    }}
                  />
                  <label
                    className="row"
                    style={{ alignItems: "center", gap: "var(--s2)", cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={newCritIsLateness}
                      onChange={(e) => {
                        setNewCritIsLateness(e.target.checked);
                      }}
                    />
                    <span>شاخص مربوط به تأخیر و انضباط ورود (پیشنهاد خودکار از حضور و غیاب)</span>
                  </label>
                  <div className="row" style={{ justifyContent: "flex-end" }}>
                    <Button type="submit" variant="primary" disabled={creatingCrit}>
                      {creatingCrit ? "در حال ایجاد..." : "ایجاد شاخص"}
                    </Button>
                  </div>
                  <ErrorLine error={critError} />
                </form>
              </Card>

              {/* Existing Criteria List */}
              <div className="stack" style={{ gap: "var(--s2)" }}>
                <h4 className="b" style={{ margin: 0 }}>
                  شاخص‌های فعال
                </h4>
                {criteria.length === 0 ? (
                  <p className="ink2">شاخصی یافت نشد.</p>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>عنوان</th>
                          <th>وزن (‰)</th>
                          <th>نوع</th>
                          <th>توضیحات</th>
                          <th>عملیات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {criteria.map((c) => (
                          <tr key={c.id}>
                            <td className="b">{c.name}</td>
                            <td>{faDigits(c.weightPerMille)}‰</td>
                            <td>
                              <Chip plain>{c.isLateness ? "تأخیر خودکار" : "دستی"}</Chip>
                            </td>
                            <td className="ink2">{c.description || "—"}</td>
                            <td>
                              <Button
                                variant="ghost"
                                onClick={() => {
                                  void handleArchiveCriterion(c.id);
                                }}
                              >
                                بایگانی
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
